import React, { useState, useEffect, useMemo, useCallback } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import {
  FiPlus, FiEye, FiSearch, FiBox, FiDollarSign, FiTag,
  FiChevronsUp, FiChevronsDown, FiFilter, FiZap, FiAlertTriangle,
  FiChevronLeft, FiChevronRight, FiLayers, FiList, FiBell,
  FiX, FiSave, FiEdit3, FiTrendingDown, FiPackage,
} from "react-icons/fi";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { Trash2 } from "lucide-react";

/* ─────────────────────────────────────────────
   DESIGN SYSTEM CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --la-teal:      #0a5556;
  --la-teal-mid:  #0c7172;
  --la-teal-lite: #14b8a6;
  --la-gold:      #FFD166;
  --la-bg:        #eef6f6;
  --la-white:     #ffffff;
  --la-border:    rgba(10,85,86,0.10);
  --la-text:      #0f2a2a;
  --la-muted:     #5a8080;
  --la-danger:    #ef4444;
  --la-success:   #10b981;
  --la-warning:   #f59e0b;
  --la-info:      #3b82f6;
  --la-radius:    20px;
  --la-trans:     all 0.18s cubic-bezier(0.4,0,0.2,1);
}

@keyframes la__fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes la__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes la__spin    { to{transform:rotate(360deg)} }
@keyframes la__modalIn { from{opacity:0;transform:scale(0.93) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }
@keyframes la__rowIn   { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
@keyframes la__bellRing {
  0%,100%{transform:rotate(0)}
  15%{transform:rotate(-22deg)}
  35%{transform:rotate(22deg)}
  55%{transform:rotate(-12deg)}
  75%{transform:rotate(12deg)}
}
@keyframes la__pulse {
  0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.35)}
  50%{box-shadow:0 0 0 7px rgba(239,68,68,0)}
}

/* ── wrapper ── */
.la__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--la-bg);
  min-height:100vh;
  animation:la__fadeUp 0.38s ease both;
}

/* ── header ── */
.la__hd {
  display:flex; align-items:flex-start; justify-content:space-between;
  gap:16px; margin-bottom:22px; flex-wrap:wrap;
}
.la__hd-left { display:flex; align-items:center; gap:14px; }
.la__hd-icon {
  width:52px; height:52px; border-radius:16px; flex-shrink:0;
  background:linear-gradient(135deg,#b91c1c,var(--la-danger));
  display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#fff;
  box-shadow:0 6px 22px rgba(239,68,68,0.35);
  animation:la__pulse 2.5s ease infinite;
}
.la__hd-title {
  font-family:'Sora',sans-serif; font-size:21px; font-weight:800;
  color:var(--la-text); letter-spacing:-0.03em; margin:0 0 3px;
}
.la__hd-sub { font-size:12.5px; color:var(--la-muted); margin:0; }

/* ── kpi row ── */
.la__kpis { display:flex; gap:14px; margin-bottom:22px; flex-wrap:wrap; }
.la__kpi {
  flex:1; min-width:160px; background:var(--la-white);
  border:1px solid var(--la-border); border-radius:18px;
  padding:18px 20px; box-shadow:0 2px 12px rgba(10,85,86,0.06);
  display:flex; align-items:center; gap:14px; position:relative; overflow:hidden;
  transition:var(--la-trans);
}
.la__kpi:hover { transform:translateY(-2px); box-shadow:0 6px 22px rgba(10,85,86,0.10); }
.la__kpi::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; }
.la__kpi--warn::before   { background:linear-gradient(90deg,#d97706,var(--la-warning),#fde68a); }
.la__kpi--danger::before { background:linear-gradient(90deg,#b91c1c,var(--la-danger),#fca5a5); }
.la__kpi--value::before  { background:linear-gradient(90deg,var(--la-teal),var(--la-teal-lite),var(--la-gold)); }

.la__kpi-icon {
  width:46px; height:46px; border-radius:13px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:19px; color:#fff;
  box-shadow:0 4px 14px rgba(0,0,0,0.18);
}
.la__kpi-icon--warn   { background:linear-gradient(135deg,#b45309,var(--la-warning)); }
.la__kpi-icon--danger { background:linear-gradient(135deg,#991b1b,var(--la-danger)); }
.la__kpi-icon--value  { background:linear-gradient(135deg,var(--la-teal),var(--la-teal-lite)); }
.la__kpi-lbl { font-size:11.5px; color:var(--la-muted); font-weight:500; margin-bottom:3px; }
.la__kpi-val { font-family:'Sora',sans-serif; font-size:20px; font-weight:800; color:var(--la-text); line-height:1; }
.la__kpi-skel {
  height:20px; width:70px; border-radius:6px; margin-top:2px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%; animation:la__shimmer 1.4s linear infinite;
}

/* ── main card ── */
.la__card {
  background:var(--la-white); border-radius:var(--la-radius);
  border:1px solid var(--la-border);
  box-shadow:0 4px 22px rgba(10,85,86,0.07); overflow:hidden; position:relative;
}
.la__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--la-danger),var(--la-warning),var(--la-gold));
}

/* ── toolbar ── */
.la__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:12px; padding:16px 20px; border-bottom:1px solid rgba(10,85,86,0.07);
  flex-wrap:wrap; background:rgba(239,68,68,0.018);
}
.la__toolbar-left  { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.la__toolbar-right { display:flex; align-items:center; gap:8px; }

.la__select {
  padding:8px 28px 8px 12px; font-size:12.5px; color:var(--la-text);
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.14);
  border-radius:10px; outline:none; cursor:pointer; appearance:none;
  font-family:'DM Sans',sans-serif; transition:var(--la-trans);
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center;
}
.la__select:focus { border-color:var(--la-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.la__select--danger { border-color:rgba(239,68,68,0.28) !important; }
.la__select--danger:focus { border-color:var(--la-danger) !important; box-shadow:0 0 0 3px rgba(239,68,68,0.10); }

.la__search {
  display:flex; align-items:center; gap:8px; background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; padding:8px 13px;
  transition:var(--la-trans); width:240px;
}
.la__search:focus-within { border-color:var(--la-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.la__search svg  { color:var(--la-muted); flex-shrink:0; }
.la__search input {
  border:none; outline:none; background:transparent;
  font-size:13px; font-family:'DM Sans',sans-serif; color:var(--la-text); flex:1;
}
.la__search input::placeholder { color:#9ababa; }
.la__clear-x { cursor:pointer; color:var(--la-muted); transition:color 0.14s; }
.la__clear-x:hover { color:var(--la-danger); }

.la__count-badge {
  font-size:11.5px; color:var(--la-muted); font-family:'Sora',sans-serif;
  font-weight:700; white-space:nowrap;
}
.la__perpage { display:flex; align-items:center; gap:6px; }
.la__perpage span { font-size:12px; color:var(--la-muted); font-family:'Sora',sans-serif; font-weight:600; }

/* ── table ── */
.la__table-wrap { overflow-x:auto; }
.la__table { width:100%; border-collapse:collapse; }
.la__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--la-teal);
  background:rgba(10,85,86,0.035); padding:13px 16px;
  white-space:nowrap; border-bottom:1px solid var(--la-border);
  cursor:pointer; user-select:none; transition:color 0.14s;
}
.la__table thead th:hover { color:var(--la-teal-lite); }
.la__table thead th:first-child { padding-left:22px; }
.la__table thead th:last-child  { padding-right:22px; text-align:center; }
.la__table thead th.th--danger  { color:var(--la-danger) !important; }

.la__sort-icon     { display:inline-flex; vertical-align:middle; margin-left:4px; opacity:0.28; }
.la__sort-icon--on { opacity:1; color:var(--la-teal-lite); }

.la__table tbody tr {
  border-bottom:1px solid rgba(10,85,86,0.05);
  transition:background 0.13s;
  animation:la__rowIn 0.22s ease both;
}
.la__table tbody tr:last-child { border-bottom:none; }
.la__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.la__table tbody tr.la__row--alert { background:rgba(239,68,68,0.045); }
.la__table tbody tr.la__row--alert:hover { background:rgba(239,68,68,0.07); }
.la__table tbody td { padding:13px 16px; font-size:13px; color:var(--la-text); vertical-align:middle; }
.la__table tbody td:first-child { padding-left:22px; }
.la__table tbody td:last-child  { padding-right:22px; text-align:center; }

/* id chip */
.la__id-chip {
  display:inline-flex; align-items:center; justify-content:center;
  font-family:'Sora',sans-serif; font-size:11px; font-weight:800;
  color:var(--la-teal); background:rgba(10,85,86,0.09);
  border:1px solid rgba(10,85,86,0.18); border-radius:8px; padding:3px 10px;
}

/* product cell */
.la__prod-cell { display:flex; align-items:center; gap:12px; }
.la__prod-thumb {
  width:40px; height:40px; border-radius:11px; object-fit:cover;
  flex-shrink:0; border:1.5px solid var(--la-border); background:#e8f4f4;
}
.la__prod-ph {
  width:40px; height:40px; border-radius:11px; flex-shrink:0;
  background:rgba(10,85,86,0.07); border:1.5px solid var(--la-border);
  display:flex; align-items:center; justify-content:center; font-size:16px; color:var(--la-muted);
}
.la__prod-name { font-size:13px; font-weight:700; color:var(--la-text); }

/* type badge */
.la__type {
  display:inline-flex; align-items:center; gap:4px; padding:3px 10px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
}
.la__type--product { background:rgba(10,85,86,0.09); color:var(--la-teal); border:1px solid rgba(10,85,86,0.18); }
.la__type--service { background:rgba(59,130,246,0.09); color:#2563eb; border:1px solid rgba(59,130,246,0.20); }

/* rate */
.la__rate { font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800; color:var(--la-teal); text-align:right; }

/* min level */
.la__min { font-family:'Sora',sans-serif; font-size:13px; font-weight:700; color:var(--la-muted); text-align:center; }

/* stock value */
.la__stock     { font-family:'Sora',sans-serif; font-size:15px; font-weight:800; text-align:center; display:inline-flex; align-items:center; gap:5px; }
.la__stock--ok  { color:var(--la-success); }
.la__stock--low { color:var(--la-danger); }
.la__stock--na  { font-size:13px; font-style:italic; color:#b0cece; font-weight:500; }

/* availability badge */
.la__avail {
  display:inline-flex; align-items:center; gap:5px; padding:4px 11px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
}
.la__avail--ok      { background:rgba(16,185,129,0.10); color:#065f46; border:1px solid rgba(16,185,129,0.25); }
.la__avail--low     { background:rgba(245,158,11,0.12); color:#92400e; border:1px solid rgba(245,158,11,0.30); }
.la__avail--out     { background:rgba(239,68,68,0.10); color:#991b1b; border:1px solid rgba(239,68,68,0.25); }
.la__avail--active  { background:rgba(10,85,86,0.09); color:var(--la-teal); border:1px solid rgba(10,85,86,0.18); }
.la__dot { width:6px; height:6px; border-radius:50%; display:inline-block; flex-shrink:0; }

/* bell action button */
.la__bell-btn {
  width:34px; height:34px; border-radius:10px;
  border:1.5px solid var(--la-border); background:rgba(10,85,86,0.05);
  display:inline-flex; align-items:center; justify-content:center;
  font-size:15px; color:var(--la-muted); cursor:pointer; transition:var(--la-trans);
}
.la__bell-btn:hover { background:rgba(239,68,68,0.10); color:var(--la-danger); border-color:rgba(239,68,68,0.28); }
.la__bell-btn:hover svg { animation:la__bellRing 0.55s ease; }

/* skeleton */
.la__skel {
  border-radius:8px; display:inline-block;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%; animation:la__shimmer 1.4s linear infinite;
}

/* empty state */
.la__empty { padding:56px 20px; text-align:center; }
.la__empty-emoji { font-size:38px; margin-bottom:12px; }
.la__empty-title { font-family:'Sora',sans-serif; font-size:15px; font-weight:700; color:var(--la-text); margin-bottom:5px; }
.la__empty-sub   { font-size:13px; color:var(--la-muted); }

/* footer */
.la__footer {
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:14px 22px; border-top:1px solid rgba(10,85,86,0.07);
  background:rgba(10,85,86,0.02); flex-wrap:wrap;
}
.la__footer-info { font-size:12px; color:var(--la-muted); font-family:'Sora',sans-serif; font-weight:600; }

/* pagination */
.la__pages { display:flex; align-items:center; gap:4px; flex-wrap:wrap; }
.la__pg-btn {
  height:32px; padding:0 10px; border-radius:9px;
  border:1.5px solid var(--la-border); background:var(--la-white); cursor:pointer;
  display:inline-flex; align-items:center; gap:4px;
  color:var(--la-muted); font-size:12px; font-family:'Sora',sans-serif; font-weight:700;
  transition:var(--la-trans);
}
.la__pg-btn:hover:not(:disabled) { border-color:var(--la-teal); color:var(--la-teal); background:rgba(10,85,86,0.05); }
.la__pg-btn:disabled { opacity:.35; cursor:not-allowed; }
.la__pg-num {
  min-width:32px; height:32px; border-radius:9px;
  border:1.5px solid var(--la-border); background:var(--la-white); cursor:pointer;
  display:inline-flex; align-items:center; justify-content:center;
  font-size:12.5px; font-family:'Sora',sans-serif; font-weight:700; color:var(--la-muted);
  transition:var(--la-trans); padding:0 8px;
}
.la__pg-num:hover { border-color:var(--la-teal); color:var(--la-teal); }
.la__pg-num--on   { background:var(--la-teal); color:#fff; border-color:var(--la-teal); box-shadow:0 3px 10px rgba(10,85,86,0.26); }

/* ══════════════════════════════════════════
   ALERT MODAL
══════════════════════════════════════════ */
.la__overlay {
  position:fixed; inset:0; z-index:1050;
  background:rgba(10,25,25,0.62); backdrop-filter:blur(7px);
  display:flex; align-items:center; justify-content:center; padding:20px;
}
.la__modal {
  background:var(--la-white); border-radius:24px; width:100%; max-width:450px;
  box-shadow:0 30px 80px rgba(239,68,68,0.20); overflow:hidden; position:relative;
  animation:la__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both;
}
.la__modal::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--la-danger),var(--la-warning),var(--la-gold));
}
.la__mhd {
  display:flex; align-items:center; justify-content:space-between;
  padding:18px 22px 14px; border-bottom:1px solid var(--la-border);
}
.la__mhd-left { display:flex; align-items:center; gap:12px; }
.la__mhd-icon {
  width:46px; height:46px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,#991b1b,var(--la-danger));
  display:flex; align-items:center; justify-content:center; font-size:20px; color:#fff;
  box-shadow:0 4px 16px rgba(239,68,68,0.32);
}
.la__mhd-title { font-family:'Sora',sans-serif; font-weight:800; font-size:15px; color:var(--la-text); margin:0 0 2px; }
.la__mhd-sub   { font-size:11.5px; color:var(--la-muted); }
.la__mhd-close {
  width:32px; height:32px; border-radius:9px; border:none; cursor:pointer;
  background:rgba(10,85,86,0.07); color:var(--la-muted);
  display:flex; align-items:center; justify-content:center; transition:var(--la-trans);
}
.la__mhd-close:hover { background:rgba(239,68,68,0.10); color:var(--la-danger); }

.la__mbody { padding:20px 22px; display:flex; flex-direction:column; gap:16px; }

.la__minfo { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.la__mchip {
  background:rgba(10,85,86,0.04); border:1.5px solid var(--la-border);
  border-radius:13px; padding:13px 16px;
}
.la__mchip-lbl {
  font-size:10px; font-family:'Sora',sans-serif; font-weight:700;
  letter-spacing:0.07em; text-transform:uppercase; color:var(--la-muted); margin-bottom:4px;
}
.la__mchip-val { font-family:'Sora',sans-serif; font-size:20px; font-weight:800; color:var(--la-text); }
.la__mchip-val--alert { color:var(--la-danger); }

.la__field-lbl {
  display:block; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
  letter-spacing:0.06em; text-transform:uppercase; color:var(--la-muted); margin-bottom:7px;
}
.la__thresh-input {
  width:100%; padding:12px 16px; font-size:16px; color:var(--la-text);
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.14); border-radius:12px;
  outline:none; font-family:'Sora',sans-serif; font-weight:800; text-align:center;
  transition:var(--la-trans);
}
.la__thresh-input:focus { border-color:var(--la-danger); box-shadow:0 0 0 3px rgba(239,68,68,0.10); background:#fff; }
.la__thresh-hint { font-size:12px; color:var(--la-muted); margin-top:7px; text-align:center; }

.la__mft {
  display:flex; gap:10px; padding:16px 22px;
  border-top:1px solid var(--la-border); background:rgba(10,85,86,0.02);
}
.la__mcancel {
  flex:1; padding:11px; border-radius:12px; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--la-muted); background:rgba(10,85,86,0.06);
  border:1.5px solid rgba(10,85,86,0.14); transition:var(--la-trans);
}
.la__mcancel:hover { background:rgba(10,85,86,0.12); color:var(--la-teal); }
.la__msave {
  flex:2; padding:11px; border-radius:12px; border:none; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,#991b1b,var(--la-danger));
  box-shadow:0 4px 16px rgba(239,68,68,0.28);
  display:flex; align-items:center; justify-content:center; gap:7px; transition:var(--la-trans);
}
.la__msave:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 7px 22px rgba(239,68,68,0.36); }
.la__msave:disabled { opacity:.5; cursor:not-allowed; transform:none; }
.la__spin {
  width:14px; height:14px; border-radius:50%;
  border:2px solid rgba(255,255,255,0.30); border-top-color:#fff;
  animation:la__spin 0.7s linear infinite;
}

@media(max-width:640px){
  .la__search   { width:100%; }
  .la__toolbar  { flex-direction:column; align-items:stretch; }
  .la__footer   { flex-direction:column; align-items:flex-start; }
  .la__minfo    { grid-template-columns:1fr; }
  .la__kpis     { flex-direction:column; }
}
`;

/* ─── utils (ALL UNCHANGED) ─── */
const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", {
    style: "currency", currency: "INR",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const getAvailInfo = (status) => {
  if (status === "Active")       return { cls: "la__avail--active", dot: "var(--la-teal)" };
  if (status === "In Stock")     return { cls: "la__avail--ok",     dot: "var(--la-success)" };
  if (status === "Low Stock")    return { cls: "la__avail--low",    dot: "var(--la-warning)" };
  if (status === "Out of Stock") return { cls: "la__avail--out",    dot: "var(--la-danger)" };
  return { cls: "la__avail--active", dot: "#94a3b8" };
};

/* ─── Skeleton rows ─── */
const SkeletonRows = ({ count = 8 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i} style={{ borderBottom: "1px solid rgba(10,85,86,0.05)" }}>
      <td style={{ padding: "14px 22px" }}>
        <span className="la__skel" style={{ width: 44, height: 24, borderRadius: 8 }} />
      </td>
      <td style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="la__skel" style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0 }} />
          <span className="la__skel" style={{ width: 130, height: 14 }} />
        </div>
      </td>
      {[65, 80, 65, 60, 55, 70].map((w, j) => (
        <td key={j} style={{ padding: "14px 16px" }}>
          <span className="la__skel" style={{ width: w, height: 22, borderRadius: 50, display: "block", margin: "0 auto" }} />
        </td>
      ))}
    </tr>
  ));

/* ─── Pagination (UNCHANGED LOGIC) ─── */
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, currentPage + 2);
  if (currentPage <= 3) { start = 1; end = Math.min(5, totalPages); }
  else if (currentPage + 2 >= totalPages) { end = totalPages; start = Math.max(1, totalPages - 4); }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="la__pages">
      <button className="la__pg-btn" disabled={currentPage === 1} onClick={() => onPageChange(1)}>First</button>
      <button className="la__pg-btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>
        <FiChevronLeft size={12} /> Prev
      </button>
      {pages.map(p => (
        <button key={p} className={`la__pg-num ${currentPage === p ? "la__pg-num--on" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
      ))}
      <button className="la__pg-btn" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}>
        Next <FiChevronRight size={12} />
      </button>
      <button className="la__pg-btn" disabled={currentPage === totalPages} onClick={() => onPageChange(totalPages)}>Last</button>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT — all backend logic UNCHANGED
═══════════════════════════════════════════════ */
function LowStockAlert() {
  const [products, setProducts]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const token = localStorage.getItem("auth_token");
  console.log("token", token);

  const [searchTerm, setSearchTerm]           = useState("");
  const [statusFilter, setStatusFilter]       = useState("All");
  const [categoryFilter, setCategoryFilter]   = useState("Product");
  const [sortBy, setSortBy]                   = useState({ key: "current_stock", direction: "asc" });
  const [currentPage, setCurrentPage]         = useState(1);
  const [itemsPerPage, setItemsPerPage]       = useState(10);
  const [showModal, setShowModal]             = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newThreshold, setNewThreshold]       = useState("");
  const [updating, setUpdating]               = useState(false);

  /* ── fetchInventory (UNCHANGED) ── */
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllProductStock`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const mappedData = res.data.data.map(item => {
          const current = parseFloat(item.current_stock || 0);
          const min = parseFloat(item.min_stock_level || 0);
          const isTracked = item.product_data?.inventory_tracked;
          let status = "In Stock";
          if (item.type === "Service") {
            status = "Active";
          } else if (isTracked) {
            if (current === 0) { status = "Out of Stock"; }
            else if (current < min) { status = "Low Stock"; }
          }
          return { ...item, calculatedStatus: status };
        });
        setProducts(mappedData);
      }
    } catch (error) { console.error("FETCH ERROR:", error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  /* ── processedProducts (UNCHANGED) ── */
  const processedProducts = useMemo(() => {
    let list = [...products];
    if (searchTerm.trim() !== "") {
      const s = searchTerm.trim().toLowerCase();
      list = list.filter(i =>
        i.product_uuid.toLowerCase().includes(s) ||
        i.product_data.name.toLowerCase().includes(s) ||
        i.type.toLowerCase().includes(s)
      );
    }
    if (statusFilter !== "All")   list = list.filter(i => i.calculatedStatus === statusFilter);
    if (categoryFilter !== "All") list = list.filter(i => i.type === categoryFilter);
    list.sort((a, b) => {
      let aVal, bVal;
      if (sortBy.key === "name")           { aVal = a.product_data.name; bVal = b.product_data.name; }
      else if (sortBy.key === "unitPrice") { aVal = a.product_data.rate; bVal = b.product_data.rate; }
      else                                 { aVal = a[sortBy.key]; bVal = b[sortBy.key]; }
      let cmp = 0;
      if (sortBy.key === "unitPrice" || sortBy.key === "current_stock") {
        cmp = Number(aVal || 0) - Number(bVal || 0);
      } else { cmp = String(aVal).localeCompare(String(bVal)); }
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [products, searchTerm, statusFilter, categoryFilter, sortBy]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, categoryFilter, sortBy, itemsPerPage]);

  const totalItems = processedProducts.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex   = Math.min(startIndex + itemsPerPage, totalItems);
  const paginated  = processedProducts.slice(startIndex, endIndex);

  /* ── kpis (UNCHANGED) ── */
  const kpis = useMemo(() => {
    const totalPhysicalItems = products.filter(p => p.type === "Product").length;
    const lowStockCount      = products.filter(p => p.calculatedStatus === "Low Stock").length;
    const outOfStockCount    = products.filter(p => p.calculatedStatus === "Out of Stock").length;
    const lowStockValue      = products
      .filter(p => p.calculatedStatus === "Low Stock")
      .reduce((s, p) => s + (p.product_data.rate * (parseFloat(p.current_stock) || 0)), 0);
    return { totalPhysicalItems, lowStockCount, outOfStockCount, lowStockValue: formatCurrency(lowStockValue) };
  }, [products]);

  /* ── handlers (ALL UNCHANGED) ── */
  const handleSort = (key) => {
    setSortBy(prev => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  };
  const allCategories  = useMemo(() => ["All", ...new Set(products.map(p => p.type))], [products]);
  const allStatuses    = ["All", "Low Stock", "Out of Stock", "In Stock", "Active"];
  const handleViewDetails = (id) => alert(`Viewing ID: ${id}`);
  const handleSetAlert    = (product) => {
    setSelectedProduct(product);
    setNewThreshold(product.min_stock_level || "");
    setShowModal(true);
  };
  const handleUpdateStockLevel = async () => {
    if (!newThreshold || isNaN(newThreshold)) return alert("Please enter a valid number");
    setUpdating(true);
    try {
      const res = await axios.post(`${baseurl}updateMinStock`, {
        product_id: selectedProduct.id,
        min_stock_level: newThreshold,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) { setShowModal(false); fetchInventory(); }
    } catch (error) { console.error("Update Error", error); }
    finally { setUpdating(false); }
  };
  const handleResetFilters = () => { setSearchTerm(""); setStatusFilter("Low Stock"); setCategoryFilter("All"); };

  const SortIcon = ({ columnKey }) => {
    if (sortBy.key !== columnKey) return <span className="la__sort-icon"><FiChevronsUp size={11} /></span>;
    return sortBy.direction === "asc"
      ? <span className="la__sort-icon la__sort-icon--on"><FiChevronsUp size={11} /></span>
      : <span className="la__sort-icon la__sort-icon--on"><FiChevronsDown size={11} /></span>;
  };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="la__wrap">

          {/* Header */}
          <div className="la__hd">
            <div className="la__hd-left">
              <div className="la__hd-icon"><FiBell /></div>
              <div>
                <div className="la__hd-title">Inventory Stock Alerts</div>
                <div className="la__hd-sub">Monitoring stock levels based on your custom thresholds</div>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="la__kpis">
            {[
              { label: "Items Currently Low", value: kpis.lowStockCount,  icon: <FiTrendingDown size={20} />, mod: "--warn",   icm: "la__kpi-icon--warn" },
              { label: "Out of Stock",         value: kpis.outOfStockCount, icon: <FiZap size={20} />,          mod: "--danger", icm: "la__kpi-icon--danger" },
              { label: "Value of Low Stock",   value: kpis.lowStockValue,   icon: <FiDollarSign size={20} />,   mod: "--value",  icm: "la__kpi-icon--value" },
            ].map((k, i) => (
              <div className={`la__kpi la__kpi${k.mod}`} key={i}>
                <div className={`la__kpi-icon ${k.icm}`}>{k.icon}</div>
                <div>
                  <div className="la__kpi-lbl">{k.label}</div>
                  {loading
                    ? <div className="la__kpi-skel" />
                    : <div className="la__kpi-val">{k.value}</div>
                  }
                </div>
              </div>
            ))}
          </div>

          {/* Main Card */}
          <div className="la__card">

            {/* Toolbar */}
            <div className="la__toolbar">
              <div className="la__toolbar-left">
                <select className="la__select la__select--danger" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="la__select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                  {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="la__search">
                  <FiSearch size={13} />
                  <input
                    placeholder="Search name, UUID, type…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && <FiX size={13} className="la__clear-x" onClick={() => setSearchTerm("")} />}
                </div>
              </div>
              <div className="la__toolbar-right">
                <span className="la__count-badge">{totalItems} items</span>
                <div className="la__perpage">
                  <span>Show</span>
                  <select className="la__select" style={{ width: "auto" }} value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))}>
                    {[5, 10, 20].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="la__table-wrap">
              <table className="la__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("id")}>ID <SortIcon columnKey="id" /></th>
                    <th onClick={() => handleSort("name")}>Item Name <SortIcon columnKey="name" /></th>
                    <th onClick={() => handleSort("type")}>Type <SortIcon columnKey="type" /></th>
                    <th style={{ textAlign: "right" }} onClick={() => handleSort("unitPrice")}>Unit Price <SortIcon columnKey="unitPrice" /></th>
                    <th style={{ textAlign: "center" }}>Min. Level</th>
                    <th className="th--danger" style={{ textAlign: "center" }} onClick={() => handleSort("current_stock")}>
                      Stock <SortIcon columnKey="current_stock" />
                    </th>
                    <th>Availability</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows count={itemsPerPage} />
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan="8">
                        <div className="la__empty">
                          <div className="la__empty-emoji">🎉</div>
                          <div className="la__empty-title">
                            {statusFilter === "Low Stock"
                              ? "All stocked up! No items are currently below minimum level."
                              : "No items match your current filters."}
                          </div>
                          <div className="la__empty-sub">Try adjusting your search or filters.</div>
                        </div>
                      </td>
                    </tr>
                  ) : paginated.map(prod => {
                    const avail   = getAvailInfo(prod.calculatedStatus);
                    const isAlert = prod.calculatedStatus === "Low Stock" || prod.calculatedStatus === "Out of Stock";
                    return (
                      <tr key={prod.id} className={isAlert ? "la__row--alert" : ""}>
                        <td><span className="la__id-chip">#{prod.id}</span></td>
                        <td>
                          <div className="la__prod-cell">
                            {prod.product_data.image_path ? (
                              <img className="la__prod-thumb" src={`${imgurl}${prod.product_data.image_path}`} alt={prod.product_data.name} />
                            ) : (
                              <div className="la__prod-ph"><FiBox size={16} /></div>
                            )}
                            <span className="la__prod-name">{prod.product_data.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`la__type ${prod.type === "Product" ? "la__type--product" : "la__type--service"}`}>
                            <FiTag size={10} /> {prod.type}
                          </span>
                        </td>
                        <td>
                          <div className="la__rate">{formatCurrency(prod.product_data.rate)}</div>
                        </td>
                        <td>
                          <div className="la__min">{prod.type === "Product" ? (prod.min_stock_level || "0.00") : "—"}</div>
                        </td>
                        <td>
                          {prod.current_stock === null ? (
                            <span className="la__stock la__stock--na">N/A</span>
                          ) : (
                            <span className={`la__stock ${isAlert ? "la__stock--low" : "la__stock--ok"}`}>
                              {isAlert && <FiAlertTriangle size={13} />}
                              {prod.current_stock}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`la__avail ${avail.cls}`}>
                            <span className="la__dot" style={{ background: avail.dot }} />
                            {prod.calculatedStatus}
                          </span>
                        </td>
                        <td>
                          <button className="la__bell-btn" title="Set Low Stock Alert" onClick={() => handleSetAlert(prod)}>
                            <FiBell size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            {!loading && (
              <div className="la__footer">
                <div className="la__footer-info">
                  Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to <strong>{endIndex}</strong> of <strong>{totalItems}</strong> entries
                </div>
                <Pagination
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>

        </div>
      </main>

      {/* Alert Modal (ALL LOGIC UNCHANGED) */}
      {showModal && (
        <div className="la__overlay" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="la__modal">

            <div className="la__mhd">
              <div className="la__mhd-left">
                <div className="la__mhd-icon"><FiBell /></div>
                <div>
                  <div className="la__mhd-title">Set Low Stock Alert</div>
                  <div className="la__mhd-sub">{selectedProduct?.product_data?.name}</div>
                </div>
              </div>
              <button className="la__mhd-close" onClick={() => setShowModal(false)}><FiX size={14} /></button>
            </div>

            <div className="la__mbody">
              <div className="la__minfo">
                <div className="la__mchip">
                  <div className="la__mchip-lbl">Current Stock</div>
                  <div className={`la__mchip-val ${parseFloat(selectedProduct?.current_stock) < parseFloat(selectedProduct?.min_stock_level) ? "la__mchip-val--alert" : ""}`}>
                    {selectedProduct?.current_stock}
                  </div>
                </div>
                <div className="la__mchip">
                  <div className="la__mchip-lbl">Current Min Level</div>
                  <div className="la__mchip-val">{selectedProduct?.min_stock_level || "0.00"}</div>
                </div>
              </div>

              <div>
                <label className="la__field-lbl">New Minimum Alert Level</label>
                <input
                  type="number"
                  className="la__thresh-input"
                  placeholder="Enter threshold quantity"
                  value={newThreshold}
                  onChange={e => setNewThreshold(e.target.value)}
                  autoFocus
                />
                <div className="la__thresh-hint">You will be alerted when stock falls to or below this quantity.</div>
              </div>
            </div>

            <div className="la__mft">
              <button className="la__mcancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="la__msave" onClick={handleUpdateStockLevel} disabled={updating}>
                {updating ? <div className="la__spin" /> : <FiSave size={14} />}
                Save Changes
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default LowStockAlert;
